import React, { useCallback } from 'react';

import isEmpty from 'lodash/isEmpty';

import { getCompletions } from '@lumapps/ai-generative-content/api';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL } from '@lumapps/translations';
import { isParagraph } from '@lumapps/wrex-typography/utils/isParagraph';
import { fromMarkdown } from '@lumapps/wrex/serialization/markdown/fromMarkdown';
import { toMarkdown } from '@lumapps/wrex/serialization/markdown/toMarkdown';
import { BaseSelection, Editor, Range, ReactEditor, Transforms, useSlate } from '@lumapps/wrex/slate';
import { withoutHistory } from '@lumapps/wrex/slate/utils/withoutHistory';
import type { Wrex } from '@lumapps/wrex/types';

export interface RefinementHistoryEntry {
    // Prompt are LumApps defined prompts (from buttons, etc.)
    prompt?: string;
    // User prompt is the one provided by the user in the dialog
    userPrompt?: string;
    // The content generated by the AI
    refinedContent: string;
}

export interface UseRefineContentOutput {
    refinementHistory: RefinementHistoryEntry[] | undefined;
    isRefiningContent: boolean;
    isPopoverOpen: boolean;
    onRefineContent: (props: {
        refinement?: string;
        isUserPrompt?: boolean;
    }) => Promise<RefinementHistoryEntry[] | undefined>;
    onInsertNewContent: (index?: number) => void;
    onCancelRefinement: () => void;
}

export const useRefineContentDialog = (): UseRefineContentOutput => {
    const editor = useSlate() as ReactEditor;

    const [isRefiningContent, setIsRefiningContent] = React.useState(false);
    const [refinementHistory, setRefinementHistory] = React.useState<RefinementHistoryEntry[] | undefined>(undefined);
    const [slateSelection, setSlateSelection] = React.useState<BaseSelection | undefined>(undefined);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    const { error } = useNotification();

    const onRefineContent: UseRefineContentOutput['onRefineContent'] = useCallback(
        async ({ refinement = '', isUserPrompt = false }): Promise<RefinementHistoryEntry[] | undefined> => {
            if (!editor.selection) {
                return undefined;
            }

            // Retrieve selected text to use it in the prompt
            const selectedText = toMarkdown(editor.getFragment() as Wrex.Nodes);
            // Store openning selection, will be used to insert the new content at the right place
            setSlateSelection(editor.selection);
            // Add a selected style to the selection, to keep it highlighted while the focus will move to the dialog
            withoutHistory(editor, () => {
                editor.addMark('selected', true);
            });
            // Clear the browser selection to avoid overlap with the custom highlight
            window.getSelection()?.empty();

            setIsPopoverOpen(true);

            // If no refinement is provided, we just open the dialog and let user type his own refinement
            if (!refinement) {
                return undefined;
            }
            setIsRefiningContent(true);

            // Compute new prompt
            const newPrompt = () => {
                /**
                 * Prompt format:
                 *
                 * General instructions
                 * <initialText>Initial text to refine</initialText>
                 * <refinement1>
                 * <userPrompt>User prompt for the first refinement</userPrompt>
                 * <refinedContent>Content after the first refinement</refinedContent>
                 * </refinement1>
                 * ...
                 * <newUserInstruction>User instruction for the new refinement</newUserInstruction>
                 */

                return `You're an expert corporate article writer. Your task is to help the user to improve his text. The gives some instruction to follow in order to improve his text. Write in a well structured markdown, answer in the language of initial text, or in the language specifically asked by the user. Go straight to the point, no need for an introduction or conclusion, directly write the content of the article. Don't directly answer the user question, but only answer with the new refined content.
You are provided with the initial text and the previous refinements iterations.
If you see @[] in the sentence, you can move his place in the sentence but you have to keep the structure (e.g. @[John Doe:10]).

The initial text you receive is the text selected by the user in the editor. It's part of a larger text, that will be given too so you can keep the context.

<entireText>${toMarkdown(editor.children as Wrex.Nodes)}</entireText>

${selectedText ? `<initialText>${selectedText}</initialText>` : ''}
${
    refinementHistory && refinementHistory.length > 0
        ? refinementHistory
              .map(
                  (refinement, index) =>
                      `<refinement${index + 1}>
<userPrompt>${refinement.prompt || refinement.userPrompt}</userPrompt>
<refinedContent>${refinement.refinedContent}</refinedContent>
</refinement${index + 1}>`,
              )
              .join('\n')
        : ''
}
<newUserInstruction>${refinement}</newUserInstruction>`;
            };

            try {
                const { data } = await getCompletions({ prompt: newPrompt() });
                const newRefinementHistory: RefinementHistoryEntry[] = [
                    ...(refinementHistory || []),
                    {
                        userPrompt: isUserPrompt ? refinement : undefined,
                        prompt: isUserPrompt ? undefined : refinement,
                        refinedContent: data.completions,
                    },
                ];
                setRefinementHistory(newRefinementHistory);
                setIsRefiningContent(false);
                return newRefinementHistory;
            } catch (e) {
                error({ translate: GLOBAL.GENERIC_ERROR });
                setIsPopoverOpen(false);
                setIsRefiningContent(false);
            }

            return undefined;
        },
        [editor, refinementHistory, error],
    );

    const resetSelection = useCallback(() => {
        // Store the insert range (because we will change selection before inserting the new content)
        const insertedRange =
            slateSelection && editor.selection
                ? {
                      // Start of initial selection
                      anchor: Range.start(slateSelection),
                      // End of inserted content
                      focus: Range.end(editor.selection),
                  }
                : undefined;

        // Remove the selected mark from the editor
        Transforms.select(editor, []);
        withoutHistory(editor, () => {
            Editor.removeMark(editor, 'selected');
        });

        if (insertedRange) {
            // Reset slate selection to the initial selection
            Transforms.select(editor, insertedRange);
        } else {
            // If no slate selection, just deselect
            editor.deselect();
        }

        ReactEditor.focus(editor);
        setSlateSelection(undefined);
    }, [editor, slateSelection]);

    const onInsertNewContent = useCallback(
        (index?: number) => {
            if (!slateSelection) {
                return;
            }
            if (refinementHistory && !isEmpty(refinementHistory) && slateSelection && index !== undefined) {
                Editor.withoutNormalizing(editor, () => {
                    Transforms.insertNodes(editor, fromMarkdown(refinementHistory[index].refinedContent), {
                        at: slateSelection,
                        select: true,
                    });
                    // Remove inserted empty paragraph
                    Transforms.removeNodes(editor, {
                        at: Range.start(slateSelection),
                        match: (node) => {
                            if (isParagraph(node)) {
                                return isEmpty((node.children[0] as Wrex.Text)?.text);
                            }
                            return false;
                        },
                    });
                    resetSelection();
                });

                setRefinementHistory(undefined);
            } else {
                error({ translate: GLOBAL.GENERIC_ERROR });
            }

            setIsPopoverOpen(false);
        },
        [editor, slateSelection, refinementHistory, resetSelection, error],
    );

    const onCancelRefinement = useCallback(() => {
        Editor.withoutNormalizing(editor, () => {
            resetSelection();
        });
        setRefinementHistory(undefined);
        setIsPopoverOpen(false);
    }, [editor, resetSelection]);

    return {
        refinementHistory,
        isRefiningContent,
        isPopoverOpen,
        onRefineContent,
        onInsertNewContent,
        onCancelRefinement,
    };
};
